<template>
    <div v-if="show" class="nevs-popup-container">
        <!-- Opened once the invoice is issued, when "send by e-mail" was ticked; the quick invoice itself is hidden
             meanwhile and closes when this does, sent or not. -->
        <ModalSendEmail :show="sendEmailModal.show" :documentId="sendEmailModal.documentId" :to="sendEmailModal.to"
                        endpoint="invoices" @done="sendEmailDone"></ModalSendEmail>
        <div v-show="!sendEmailModal.show" class="nevs-popup quick-invoice-popup">
            <div class="quick-invoice-content">
                <h2 class="quick-invoice-title">{{ $LANG.Get('labels.quickInvoiceTitle') }}</h2>

                <div class="quick-invoice-disclaimer">
                    <i class="fa-solid fa-circle-info"></i>
                    <span>{{ $LANG.Get('texts.quickInvoiceDisclaimer') }}</span>
                </div>

                <NevsSelect v-model="personId" :ajax="'select/persons'" :nullable="true"
                            :label="$LANG.Get('labels.person') + ':'" :error="personError"></NevsSelect>

                <div class="quick-invoice-header-row">
                    <NevsSelect v-model="invoiceSequenceId" :options="sequences"
                                :label="$LANG.Get('fields.sequence') + ':'"
                                :error="sequenceError"></NevsSelect>
                    <NevsSelect v-model="paymentMethodId" :ajax="'select/payment-methods'"
                                :label="$LANG.Get('fields.paymentMethod') + ':'"
                                :error="paymentMethodError"></NevsSelect>
                </div>

                <NevsSelect v-model="selectedThing" :ajax="'select/things'" :nullable="true"
                            :label="$LANG.Get('labels.selectItem') + ':'"></NevsSelect>

                <div v-if="itemsError !== ''" class="quick-invoice-items-error nevs-text-error">{{ itemsError }}</div>

                <table v-if="items.length > 0" class="quick-invoice-table">
                    <thead>
                    <tr>
                        <th>{{ $LANG.Get('fields.description') }}</th>
                        <th class="quick-invoice-price-column">{{ $LANG.Get('fields.price') }} (EUR)</th>
                        <th class="quick-invoice-quantity-column">{{ $LANG.Get('fields.quantity') }}</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, key) in items" :key="key">
                        <td>{{ item.description }}</td>
                        <td class="quick-invoice-price-column">{{ $HELPERS.FormatMoney(itemDisplayPrice(item)) }}</td>
                        <td class="quick-invoice-quantity-column">
                            <NevsNumberField v-model="item.quantity" :decimal-places="2"></NevsNumberField>
                        </td>
                        <td class="quick-invoice-actions">
                            <span :title="$LANG.Get('tooltips.removeItem')" class="quick-invoice-remove"
                                  @click="removeItem(key)"><i class="fa-solid fa-trash"></i></span>
                        </td>
                    </tr>
                    </tbody>
                </table>

                <table v-if="items.length > 0" class="quick-invoice-summary-table">
                    <tbody>
                    <tr v-for="(base, key) in taxBases" :key="key">
                        <td>{{ $LANG.Get('labels.taxBase') }} {{ base.base }} (EUR):</td>
                        <td class="quick-invoice-summary-value">{{ $HELPERS.FormatMoney(base.value) }}</td>
                    </tr>
                    <tr class="quick-invoice-summary-total">
                        <td>{{ $LANG.Get('fields.total') }} (EUR):</td>
                        <td class="quick-invoice-summary-value">{{ $HELPERS.FormatMoney(total) }}</td>
                    </tr>
                    </tbody>
                </table>

                <NevsCheckbox v-model="sendByEmail" :label="$LANG.Get('labels.sendByEmail')"
                              class="quick-invoice-send-email"></NevsCheckbox>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancel">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.close') }}
                </NevsButton>
                <NevsButton class="success" :class="{'quick-invoice-disabled': submitting}" @click="submit">
                    <i class="fa-solid fa-file-invoice"></i>
                    {{ $LANG.Get('buttons.createInvoice') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import Big from 'big.js';
import Invoice from "@/models/Invoice.js";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import ModalSendEmail from "@/components/modals/ModalSendEmail.vue";

export default {
    name: "ModalQuickInvoice",
    components: {ModalSendEmail, NevsCheckbox, NevsNumberField, NevsSelect, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    emits: ['done'],
    data() {
        return {
            personId: null,
            selectedThing: null,
            items: [],
            // The date memberships from this invoice start on, resolved right before the invoice is created.
            membershipStartDate: null,
            submitting: false,
            sendByEmail: false,
            // The customer's official addresses, fetched with the customer, to pre-fill the e-mail's recipient.
            customerEmails: '',
            sendEmailModal: {
                show: false,
                documentId: null,
                to: ''
            },
            // The sequences this user may issue from, straight off their session - the same list the invoice
            // screen offers. Only their fiskal flag has to be fetched, and only for the selected one.
            sequences: [],
            invoiceSequenceId: null,
            selectedSequenceIsFiskal: false,
            paymentMethodId: null,
            personError: '',
            sequenceError: '',
            paymentMethodError: '',
            itemsError: ''
        }
    },
    computed: {
        // Whether the invoice charges tax. The quick invoice never uses "prijenos porezne obveze", so the
        // client's tax system is the whole answer - and when it is off every item is added with tax 0.
        taxed() {
            return this.$store.state.user.client.tax_system === true;
        },
        // The tax bases of the invoice about to be created, one per tax rate in use, worked out the way
        // EntityInvoice.calculateTotals() does so the amounts shown here are the ones the invoice will carry.
        // The quick invoice has no invoice discount and no per-item discount, which is what is left out.
        taxBases() {
            if (!this.taxed) return [];
            let bases = [];
            for (let item of this.items) {
                let value = this.itemTotal(item);
                let existing = bases.find((base) => base.base === item.tax);
                if (existing === undefined) {
                    bases.push({base: item.tax, value: value.toString()});
                } else {
                    existing.value = Big(existing.value).add(value).toString();
                }
            }
            return bases;
        },
        // The amount the invoice will be issued for: each tax base plus its tax when the client charges tax,
        // the plain sum of the item lines when it does not.
        total() {
            let total = Big(0);
            if (this.taxed) {
                for (let base of this.taxBases) {
                    let tax = Big(base.value).times(Big(base.base)).div(Big(100));
                    total = total.add(tax.toFixed(2)).add(Big(base.value));
                }
            } else {
                for (let item of this.items) {
                    total = total.add(this.itemTotal(item));
                }
            }
            return total.toFixed(2);
        }
    },
    watch: {
        show() {
            if (this.show) {
                // Start every quick invoice from a clean slate, with the sequence and the payment method the
                // client issues most invoices with already picked.
                this.personId = null;
                this.selectedThing = null;
                this.items = [];
                this.membershipStartDate = null;
                this.submitting = false;
                this.sendByEmail = false;
                this.customerEmails = '';
                this.sendEmailModal = {show: false, documentId: null, to: ''};
                this.sequences = this.$store.state.user.invoice_sequences;
                this.invoiceSequenceId = this.$store.state.user.default_invoice_sequence_id;
                this.paymentMethodId = this.$store.state.user.client.default_payment_method_id
                    ? this.$store.state.user.client.default_payment_method_id : 1;
                this.personError = '';
                this.sequenceError = '';
                this.paymentMethodError = '';
                this.itemsError = '';
            }
        },
        invoiceSequenceId() {
            this.loadSequenceFiskal();
        },
        selectedThing(value) {
            // Only catalog things (PRODUCT_/MEMBERSHIP_/PACKAGE_) are supported here.
            if (value !== null && value !== -1) {
                this.addItem(value);
            }
            if (value === -1) {
                this.$nextTick(() => {
                    this.selectedThing = null;
                });
            }
        }
    },
    methods: {
        // Whether the picked sequence fiscalizes. Only the sequence's id travels in the session, so the flag is
        // fetched the same way the invoice screen fetches it, and only for the sequence actually selected.
        loadSequenceFiskal() {
            if (this.invoiceSequenceId === null || this.invoiceSequenceId === 0) {
                this.selectedSequenceIsFiskal = false;
                return;
            }
            let vm = this;
            this.$API.APICall('get', 'invoice-sequences/' + this.invoiceSequenceId, {}, (data, success) => {
                vm.selectedSequenceIsFiskal = (success && data.invoice_sequence !== null && data.invoice_sequence.fiskal);
            }, false);
        },
        addItem(thing) {
            let vm = this;
            let parts = String(thing).split('_');
            let type = parts[0];
            let id = parts[1];
            let client = this.$store.state.user.client;
            let defaultMeasurementUnitId = (client.default_measurement_unit_id !== null) ? client.default_measurement_unit_id : 1;

            let endpoint = null;
            let key = null;
            let membershipId = null;
            let packageId = null;
            let productId = null;
            if (type === 'PRODUCT') {
                endpoint = 'products/';
                key = 'product';
            } else if (type === 'MEMBERSHIP') {
                endpoint = 'memberships/';
                key = 'membership';
            } else if (type === 'PACKAGE') {
                endpoint = 'packages/';
                key = 'package';
            } else {
                this.selectedThing = null;
                return;
            }

            this.$API.APICall('get', endpoint + id, {}, (data, success) => {
                if (success) {
                    let thingData = data[key];
                    if (type === 'PRODUCT') productId = thingData.id;
                    if (type === 'MEMBERSHIP') membershipId = thingData.id;
                    if (type === 'PACKAGE') packageId = thingData.id;

                    let newItem = {
                        description: thingData.name,
                        details: '',
                        code_kpd: thingData.code_kpd ? thingData.code_kpd : (client.default_kpd_code || ''),
                        // currency is always EUR (rate 1) so the price is used as stored.
                        price: thingData.price,
                        quantity: 1,
                        tax: client.tax_system ? thingData.tax : 0,
                        discount: 0,
                        measurement_unit_id: (type === 'PRODUCT') ? thingData.measurement_unit_id : defaultMeasurementUnitId,
                        total: 0,
                        product_id: productId,
                        membership_id: membershipId,
                        package_id: packageId
                    };
                    vm.items.push(newItem);
                    vm.itemsError = '';
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                vm.$nextTick(() => {
                    vm.selectedThing = null;
                });
            }, false);
        },
        // The price shown on the line. A client in the tax system sells at the price with tax, so the catalog
        // price - which is stored and invoiced net - is grossed up for display only. The invoice is still
        // built from the net price and the item's tax rate, the way the invoice screen builds it, and the tax
        // bases under the table are net for the same reason.
        itemDisplayPrice(item) {
            if (!this.taxed) return item.price;
            return Big(item.price).times(Big(100).plus(Big(item.tax))).div(Big(100)).toFixed(2);
        },
        // One item's line: price times quantity, rounded to the cent before anything is summed - the same
        // order EntityInvoice rounds in, so the totals cannot drift apart by a cent.
        itemTotal(item) {
            return Big(Big(item.price).times(Big(item.quantity)).toFixed(2));
        },
        removeItem(index) {
            this.items.splice(index, 1);
        },
        cancel() {
            this.$emit('done');
        },
        submit() {
            if (this.submitting) return;

            let valid = true;
            if (this.personId === null || this.personId === '') {
                valid = false;
                this.personError = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.personError = '';
            }
            if (this.invoiceSequenceId === null || this.invoiceSequenceId === 0) {
                valid = false;
                this.sequenceError = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.sequenceError = '';
            }
            if (this.items.length === 0) {
                valid = false;
                this.itemsError = this.$LANG.Get('labels.noItems');
            } else {
                this.itemsError = '';
            }
            // Everything but a transfer to the bank account has to be fiscalized, so those payment methods need a
            // fiskal sequence. The quick invoice always bills a person, which is the case that from the F2
            // transition on drags a bank transfer in as well. Mirrors the same check on the invoice screen.
            let paymentMethodTerm = (this.$HELPERS.CheckF2(this, moment().format('YYYY-MM-DD HH:mm:ss')) > 0) ? 0 : 1;
            if (this.paymentMethodId > paymentMethodTerm && !this.selectedSequenceIsFiskal) {
                valid = false;
                this.paymentMethodError = this.$LANG.Get('labels.fiskalNeeded');
            } else {
                this.paymentMethodError = '';
            }
            if (!valid) return;

            let vm = this;
            this.submitting = true;

            // The invoice needs the person's customer.
            this.$API.APICall('get', 'persons/customer/' + this.personId, {}, (data, success) => {
                if (!success || data.customer_id === null) {
                    vm.submitting = false;
                    vm.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: vm.$LANG.Get('alerts.personHasNoCustomer')});
                    return;
                }
                vm.customerEmails = data.customer_emails ?? '';
                vm.resolveMembershipStartDate(() => {
                    vm.createInvoice(data.customer_id);
                });
            });
        },
        // Memberships sold here start on the first day the person has no membership running: today, or the day
        // after the running ones end (and after any renewal already queued behind them), so a new membership never
        // overlaps what is already paid for. See $HELPERS.FirstFreeMembershipDate.
        resolveMembershipStartDate(done) {
            let today = moment().format('YYYY-MM-DD');
            if (!this.items.some((item) => item.membership_id)) {
                this.membershipStartDate = today;
                done();
                return;
            }

            let vm = this;
            this.$API.APICall('get', 'person-memberships/by-person/' + this.personId, {}, (data, success) => {
                let startDate = today;
                if (success && Array.isArray(data.records)) {
                    startDate = vm.$HELPERS.FirstFreeMembershipDate(data.records);
                }
                // A failed lookup must not stop the invoice: fall back to today, as before.
                vm.membershipStartDate = startDate;
                done();
            });
        },
        createInvoice(customerId) {
            let vm = this;
            let client = this.$store.state.user.client;
            let paymentMethodId = this.paymentMethodId;
            // The document format follows the payment method, exactly as it does on the invoice screen.
            let documentFormatId = client['invoice_document_format_id_' + paymentMethodId];
            if (!documentFormatId) documentFormatId = 1;

            let invoice = new Invoice({});
            invoice.customer_id = customerId;
            invoice.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
            invoice.due_date = moment().format('YYYY-MM-DD');
            invoice.date_of_delivery = moment().format('YYYY-MM-DD');
            invoice.currency = 'EUR';
            invoice.currency_rate = 1;
            invoice.invoice_sequence_id = this.invoiceSequenceId;
            invoice.locale = 'hr';
            invoice.payment_method_id = paymentMethodId;
            invoice.document_format_id = documentFormatId;
            invoice.footer = client.invoice_footer_template_hr;
            invoice.discount = 0;
            invoice.tax_system = client.tax_system;
            invoice.iban = client.iban[0];
            invoice.swift = client.swift[0];
            invoice.invoice_status_id = 1;
            invoice.ppo = false;
            invoice.ppo_text = '';
            invoice.items = JSON.parse(JSON.stringify(this.items));

            // Create + fiscalize through the shared helper so this uses exactly the same sequence as
            // EntityInvoice. The parts specific to the quick invoice stay here as hooks: on any non-200 from the
            // creation or fiskal route we show the response content; on success we assign things and open the PDF.
            this.$HELPERS.SubmitInvoice(vm, {
                method: 'post',
                payload: JSON.parse(JSON.stringify(invoice)),
                onSuccess: (createdInvoice) => {
                    vm.finalizeInvoice(createdInvoice);
                },
                onError: (data) => {
                    vm.submitting = false;
                    vm.showResponsePopup(data);
                },
                onFiskalError: (data) => {
                    vm.showResponsePopup(data);
                }
            });
        },
        finalizeInvoice(invoice) {
            let vm = this;
            let assignments = this.buildAssignments();
            if (assignments.length > 0) {
                this.$API.APICall('post', 'invoices/assign-things', {
                    invoice_id: invoice.id,
                    assignments: assignments
                }, (assignData, assignSuccess) => {
                    if (!assignSuccess) {
                        vm.$HELPERS.HandleServerError(vm, assignData);
                    }
                    vm.openInvoicePdf(invoice);
                });
            } else {
                this.openInvoicePdf(invoice);
            }
        },
        // Shows a popup with the raw content of a server response (used when the invoice creation or fiskal
        // route returns a non-200 code).
        showResponsePopup(data) {
            let text = (typeof data === 'string') ? data : JSON.stringify(data);
            this.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: text});
        },
        buildAssignments() {
            // Memberships and packages on the invoice are assigned to the selected person, one per unit,
            // mirroring EntityInvoice's assign-things flow. Memberships start on the date resolved in
            // resolveMembershipStartDate(): today, or the day after the person's current memberships end.
            let startDate = (this.membershipStartDate !== null) ? this.membershipStartDate : moment().format('YYYY-MM-DD');
            let assignments = [];
            for (let item of this.items) {
                let count = Math.round(item.quantity);
                if (count < 1) continue;
                for (let i = 0; i < count; i++) {
                    if (item.membership_id) {
                        assignments.push({
                            type: 'membership',
                            person_id: this.personId,
                            membership_id: item.membership_id,
                            start_date: startDate
                        });
                    } else if (item.package_id) {
                        assignments.push({
                            type: 'package',
                            person_id: this.personId,
                            package_id: item.package_id
                        });
                    }
                }
            }
            return assignments;
        },
        openInvoicePdf(invoice) {
            let pdfLink = this.$store.state.settings.API_BASE + 'invoices/pdf/' + this.$store.state.user.client.hash + '/' + invoice.hash;
            window.open(pdfLink);
            this.submitting = false;
            if (this.sendByEmail) {
                this.sendEmailModal = {show: true, documentId: invoice.id, to: this.customerEmails};
                return;
            }
            this.$emit('done');
        },
        // The invoice is issued either way, so the quick invoice closes whether the e-mail was sent or cancelled.
        sendEmailDone() {
            this.sendEmailModal = {show: false, documentId: null, to: ''};
            this.$emit('done');
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.quick-invoice-popup {
    width: 60%;
    max-width: 720px;
}

.quick-invoice-content {
    padding: 25px 25px 5px;
}

.quick-invoice-title {
    margin: 0 0 15px;
    font-size: 1.4em;
}

.quick-invoice-disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 12px 14px;
    margin-bottom: 20px;
    font-size: 0.9em;
    line-height: 1.45;
}

.quick-invoice-disclaimer i {
    margin-top: 2px;
    flex-shrink: 0;
}

/* The sequence and the payment method are short header fields, so they share one row - stacking on narrow
   screens where two dropdowns side by side stop being readable. */
.quick-invoice-header-row {
    display: flex;
    gap: 15px;
}

.quick-invoice-header-row > * {
    flex: 1;
    min-width: 0;
}

.quick-invoice-items-error {
    margin: 5px 0;
}

.quick-invoice-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
}

.quick-invoice-table th {
    text-align: left;
    padding: 0 15px 10px 0;
    border-bottom: 2px solid $sand-400;
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.quick-invoice-table td {
    text-align: left;
    padding: 10px 15px 10px 0;
    border-bottom: 1px solid $sand-300;
    vertical-align: middle;
}

/* The price the item is billed at, shown with tax when the client charges it (see itemDisplayPrice). */
.quick-invoice-price-column {
    width: 120px;
    text-align: right;
}

.quick-invoice-quantity-column {
    width: 140px;
}

.quick-invoice-actions {
    width: 40px;
    text-align: center;
}

.quick-invoice-remove {
    cursor: pointer;
    color: $ink-faint;
}

.quick-invoice-remove:hover {
    color: $danger;
}

.quick-invoice-send-email {
    margin-top: 15px;
}

.quick-invoice-summary-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
}

.quick-invoice-summary-table td {
    padding: 6px 15px 6px 0;
    text-align: left;
}

.quick-invoice-summary-value {
    width: 160px;
    text-align: right;
}

.quick-invoice-summary-total td {
    font-weight: bold;
    border-top: 2px solid $sand-400;
}

.quick-invoice-disabled {
    opacity: 0.6;
    pointer-events: none;
}

@media only screen and (max-width: 850px) {
    .quick-invoice-popup {
        width: 90%;
    }

    .quick-invoice-header-row {
        display: block;
    }
}
</style>
